import { containingArea, pointInPolygon, segmentInsidePolygon, perspectiveScale } from "../scene-core/index.js";

const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const samePoint = (a, b, epsilon = 1e-7) => dist(a, b) <= epsilon;
export const DEFAULT_MOVEMENT_SPEED = .09;
export * from "./interaction-icons.js";
export * from "./interaction-hover.js";
export * from "./operations.js";
export * from "./weather.js";
export * from "./animated-props.js";

function distanceToSegment(point, a, b) {
  const dx = b.x - a.x, dy = b.y - a.y, lengthSquared = dx * dx + dy * dy;
  const t = lengthSquared ? Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / lengthSquared)) : 0;
  return dist(point, { x: a.x + dx * t, y: a.y + dy * t });
}

export function clearanceFromBoundary(point, polygon) {
  return Math.min(...polygon.map((a, index) => distanceToSegment(point, a, polygon[(index + 1) % polygon.length])));
}

export function segmentWalkable(a, b, polygon, safetyMargin = 0) {
  if (!segmentInsidePolygon(a, b, polygon)) return false;
  if (safetyMargin <= 0 || samePoint(a, b)) return true;
  const length = dist(a, b), samples = Math.max(2, Math.ceil(length / Math.max(safetyMargin, .001)));
  for (let index = 1; index < samples; index++) {
    const t = index / samples, point = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
    if (clearanceFromBoundary(point, polygon) + 1e-8 < safetyMargin) return false;
  }
  return true;
}

export function nudgeInside(point, polygon, safetyMargin = .0025) {
  if (!pointInPolygon(point, polygon)) return null;
  if (safetyMargin <= 0 || clearanceFromBoundary(point, polygon) >= safetyMargin) return { ...point };
  let best = null;
  for (const radius of [safetyMargin, safetyMargin * 1.5, safetyMargin * 2, safetyMargin * 3, safetyMargin * 4]) {
    for (let index = 0; index < 32; index++) {
      const angle = index / 32 * Math.PI * 2;
      const candidate = { x: point.x + Math.cos(angle) * radius, y: point.y + Math.sin(angle) * radius };
      if (!pointInPolygon(candidate, polygon)) continue;
      const clearance = clearanceFromBoundary(candidate, polygon);
      if (clearance >= safetyMargin && (!best || dist(point, candidate) < dist(point, best))) best = candidate;
    }
    if (best) return best;
  }
  return null;
}

function simplifyPath(start, path, polygon, safetyMargin) {
  const simplified = [];
  let anchor = start, index = 0;
  while (index < path.length) {
    let furthest = index;
    for (let candidate = path.length - 1; candidate >= index; candidate--) {
      if (segmentWalkable(anchor, path[candidate], polygon, safetyMargin)) { furthest = candidate; break; }
    }
    simplified.push(path[furthest]); anchor = path[furthest]; index = furthest + 1;
  }
  return simplified;
}

export function findRoute(scene, from, to, { safetyMargin = .0025 } = {}) {
  const startArea = containingArea(scene, from), targetArea = containingArea(scene, to);
  const result = {
    path: null, polygonId: startArea?.id || null, directRejected: false,
    reason: null, requestedTarget: { ...to }, start: { ...from }, safetyMargin
  };
  if (!startArea) { result.reason = "start-outside-walkable"; return result; }
  if (!targetArea) { result.reason = "target-outside-walkable"; return result; }
  if (targetArea !== startArea) { result.reason = "different-walkable-polygons"; return result; }
  const polygon = startArea.vertices;
  const safeStart = nudgeInside(from, polygon, safetyMargin) || nudgeInside(from, polygon, 0);
  const safeTarget = nudgeInside(to, polygon, safetyMargin);
  if (!safeStart || !safeTarget) { result.reason = "safety-margin-unavailable"; return result; }
  result.correctedStart = safeStart; result.correctedTarget = safeTarget;
  if (samePoint(safeStart, safeTarget)) { result.path = [safeTarget]; return result; }
  if (segmentWalkable(safeStart, safeTarget, polygon, safetyMargin)) { result.path = [safeTarget]; return result; }
  result.directRejected = true;

  const insetVertices = [];
  for (const vertex of polygon) {
    const inset = nudgeInside(vertex, polygon, safetyMargin);
    if (inset && !insetVertices.some(point => samePoint(point, inset))) insetVertices.push(inset);
  }
  const nodes = [safeStart, ...insetVertices, safeTarget], edges = nodes.map(() => []);
  for (let left = 0; left < nodes.length; left++) for (let right = left + 1; right < nodes.length; right++) {
    if (!segmentWalkable(nodes[left], nodes[right], polygon, safetyMargin)) continue;
    const weight = dist(nodes[left], nodes[right]);
    edges[left].push([right, weight]); edges[right].push([left, weight]);
  }
  const costs = nodes.map(() => Infinity), previous = nodes.map(() => -1), open = new Set(nodes.map((_, index) => index));
  costs[0] = 0;
  while (open.size) {
    let current = -1;
    for (const candidate of open) if (current < 0 || costs[candidate] < costs[current]) current = candidate;
    if (!Number.isFinite(costs[current])) break;
    open.delete(current);
    if (current === nodes.length - 1) break;
    for (const [neighbor, weight] of edges[current]) if (open.has(neighbor) && costs[current] + weight < costs[neighbor]) {
      costs[neighbor] = costs[current] + weight; previous[neighbor] = current;
    }
  }
  if (!Number.isFinite(costs.at(-1))) { result.reason = "no-valid-route"; return result; }
  const rawPath = [];
  for (let current = nodes.length - 1; current > 0; current = previous[current]) {
    if (current < 0) { result.reason = "no-valid-route"; return result; }
    rawPath.unshift(nodes[current]);
  }
  const path = simplifyPath(safeStart, rawPath, polygon, safetyMargin);
  let anchor = safeStart;
  for (const waypoint of path) {
    if (!segmentWalkable(anchor, waypoint, polygon, safetyMargin)) { result.reason = "invalid-route-after-simplification"; return result; }
    anchor = waypoint;
  }
  result.path = path;
  return result;
}

export function routeWithinScene(scene, from, to, options) {
  return findRoute(scene, from, to, options).path;
}

export class CharacterController {
  constructor(scene, onChange = () => {}, options = {}) {
    this.scene = scene; this.onChange = onChange; this.position = { x: .5, y: .75 }; this.path = [];
    this.speed = options.movementSpeed ?? DEFAULT_MOVEMENT_SPEED; this.scale = 1; this.safetyMargin = options.safetyMargin ?? .0025; this.routeDebug = null;
    this.movementVector = { x: 1, y: 0 }; this.actualMovementSpeed = 0; this.turning = false;this.routeId=0;
  }
  get isMoving() { return this.path.length > 0; }
  get movementSpeed() { return this.isMoving ? (this.actualMovementSpeed || this.speed) : 0; }
  get configuredMovementSpeed() { return this.speed; }
  spawn(id = this.scene.player.defaultSpawn) {
    const spawn = this.scene.player.spawns.find(x => x.id === id) || this.scene.player.spawns[0];
    if (spawn) this.position = { x: spawn.x, y: spawn.y };
    this.path = []; this.after = null;
    this.movementVector = { x: spawn?.facing === "left" ? -1 : 1, y: 0 }; this.actualMovementSpeed = 0; this.turning = false; this.turnAfter = null;
    this.scale = perspectiveScale(this.scene.player.perspective, this.position.y); this.onChange(this);
  }
  walkTo(target, after = null) {
    this.routeId++;
    this.path = []; this.after = null;
    const route = findRoute(this.scene, this.position, target, { safetyMargin: this.safetyMargin });
    this.routeDebug = route;
    if (!route.path) { this.onChange(this); return false; }
    this.position = route.correctedStart || this.position;
    this.path = route.path.map(point => ({ ...point })); this.after = after; this.turning = false; this.turnAfter = null;
    this.updateMovementVector(); this.onChange(this); return true;
  }
  walkControlledTo(target, after = null) {
    if (!target || !Number.isFinite(target.x) || !Number.isFinite(target.y)) return false;
    this.routeId++;this.path=[{x:target.x,y:target.y}];this.after=after;this.turning=false;this.turnAfter=null;
    this.routeDebug={path:[{...target}],polygonId:"controlled-prop-move",directRejected:false,reason:null,start:{...this.position},requestedTarget:{...target}};
    this.updateMovementVector();this.onChange(this);return true;
  }
  updateMovementVector() {
    if (!this.path.length) return this.movementVector;
    const target = this.path[0], dx = target.x - this.position.x, dy = target.y - this.position.y, length = Math.hypot(dx, dy);
    if (length > 1e-7) this.movementVector = { x: dx / length, y: dy / length };
    return this.movementVector;
  }
  faceTo(point, after = null) {
    const dx = point.x - this.position.x, dy = point.y - this.position.y, length = Math.hypot(dx, dy);
    if (length <= 1e-7) { after?.(); return false; }
    this.movementVector = { x: dx / length, y: dy / length }; this.turning = true; this.turnAfter = after; this.actualMovementSpeed = 0; this.onChange(this); return true;
  }
  cancel({notify=true}={}) { const active=this.isMoving||this.turning||this.after||this.turnAfter;this.path=[];this.after=null;this.turning=false;this.turnAfter=null;this.actualMovementSpeed=0;if(active&&notify)this.onChange(this);return!!active; }
  finishTurn() {
    if (!this.turning) return;
    this.turning = false; const fn = this.turnAfter; this.turnAfter = null; this.onChange(this); fn?.();
  }
  update(dt) {
    if (!this.path.length) { this.actualMovementSpeed = 0; return; }
    const target = this.path[0], distance = dist(this.position, target), step = this.speed * dt;
    this.updateMovementVector(); this.facing = this.movementVector.x < 0 ? "left" : "right";
    const before = { ...this.position };
    if (distance <= step) {
      this.position = target; this.path.shift(); this.updateMovementVector();
      if (!this.path.length) { const fn = this.after; this.after = null; fn?.(); }
    } else {
      this.position.x += (target.x - this.position.x) / distance * step;
      this.position.y += (target.y - this.position.y) / distance * step;
    }
    this.actualMovementSpeed = dt > 0 ? dist(before, this.position) / dt : 0;
    this.scale = perspectiveScale(this.scene.player.perspective, this.position.y); this.onChange(this);
  }
}

export { containingArea, pointInPolygon, perspectiveScale };
export { BackgroundMedia, createBackgroundMedia, normalizedImageConfig, chooseMediaQuality, BACKGROUND_MEDIA_LIMITS } from "./background-media.js";
export { OcclusionLayer, createOcclusionLayer, enabledOccluders } from "./occlusion.js";
export { CharacterLighting, createCharacterLighting, sampleLighting, zoneInfluence, normalizeLighting, DEFAULT_LIGHTING } from "./character-lighting.js";
export { WeatherController, createWeatherController, normalizeWeather, WEATHER_DEFAULTS, WEATHER_QUALITY, createWeatherRandom, weatherExclusionHit } from "./weather.js";
